// Townsfolk on screen: one Character per body. Each is a clone of its costume's GLB (client/assets/chars/<id>.glb — the
// RIG.md skeleton, rigid skinning), playing the shared CMU clip set (art/anim/clips.glb → client/assets/anim/clips.glb)
// through its own AnimationMixer, wearing a hue-keeping inverted-hull outline and a contact blob (ART_BIBLE §5.5, §7).
// The sim's anim state picks the clip; speeds are matched to the mocap's natural stride so feet don't skate.

import * as THREE from "three";
import { clone as cloneSkinned } from "three/examples/jsm/utils/SkeletonUtils.js";
import type { GLTF } from "three/examples/jsm/loaders/GLTFLoader.js";
import { gradientMap, glowify, type Tier } from "./core";
import { Ragdoll } from "./ragdoll";
import * as T from "../../../shared/tuning";

export type AnimName = "idle" | "walk" | "run" | "browse" | "chat" | "sit_bench" | "shoelace" | "look_around" | "dance" | "sit_ground" | "wave" | "jump" | "clap" | "angry" | "getup" | "cheer" | "point" | "freeze" | "ragdoll";
const ONCE: Partial<Record<AnimName, true>> = { shoelace: true, sit_ground: true, angry: true, getup: true, point: true };
const STRIDE = { walk: 0.67, run: 1.71 };       // m/s of the mocap at scale 1 (art/anim/CLIPS.md)
// once-clips are fitted to the sim's state lengths (a 4.6 s CMU get-up must finish inside the sim's GETUP window)
const FIT: Partial<Record<AnimName, number>> = { getup: T.GETUP_SEC, angry: T.ANGRY_SEC, shoelace: T.SHOELACE_SEC, point: T.POINT_SEC + 0.1, sit_ground: T.CALL_HOLD * 0.85 };
const SKIN = ["#F7D5B5", "#E6B48C", "#C48A60", "#8E5E3F"].map((c) => new THREE.Color(c));
export const HAIR: Record<string, string> = { hair_brown: "#5A3D2E", hair_dark: "#3A2E2C", hair_auburn: "#A5532F", hair_blond: "#E9C47C", hair_silver: "#CFD0DC", hair_white: "#F4F2EE" };
const PAINT_HI = new THREE.Color("#FFF8EA");
/** recolour a character's painted vertex colours (manifest conventions.skin_hair_body_tint):
 *  for each vertex with _tint == g: colour = mix(tone[g], #FFF8EA, _paint.x) * _paint.y   — g: 1 skin, 2 hair, 3 body, 4 body_dark.
 *  One colour attribute per (geometry, tones) is shared by every body that looks the same. */
const recolourCache = new Map<string, THREE.BufferAttribute>();
export function recolour(geo: THREE.BufferGeometry, tones: Partial<Record<1 | 2 | 3 | 4, THREE.Color>>, key: string): THREE.BufferGeometry {
  const tint = geo.getAttribute("_tint"), paint = geo.getAttribute("_paint"), col = geo.getAttribute("color");
  if (!tint || !paint || !col) return geo;
  const ck = geo.uuid + "|" + key;
  let attr = recolourCache.get(ck);
  if (!attr) {
    const n = col.count, size = col.itemSize, out = new Float32Array(n * size);
    const c = new THREE.Color();
    for (let i = 0; i < n; i++) {
      const g = Math.round(tint.getX(i)) as 0 | 1 | 2 | 3 | 4;
      const tone = g ? tones[g as 1 | 2 | 3 | 4] : undefined;
      if (tone) { c.copy(tone).lerp(PAINT_HI, paint.getX(i)).multiplyScalar(paint.getY(i)); out[i * size] = c.r; out[i * size + 1] = c.g; out[i * size + 2] = c.b; if (size === 4) out[i * size + 3] = 1; }
      else for (let k = 0; k < size; k++) out[i * size + k] = col.getComponent(i, k);
    }
    attr = new THREE.BufferAttribute(out, size); recolourCache.set(ck, attr);
  }
  const g2 = new THREE.BufferGeometry();
  for (const [name, a] of Object.entries(geo.attributes)) g2.setAttribute(name, name === "color" ? attr : a);
  g2.setIndex(geo.index); g2.groups = geo.groups; g2.boundingSphere = geo.boundingSphere; g2.boundingBox = geo.boundingBox;
  return g2;
}
const OUTLINE_K = new THREE.Color().setRGB(0.336, 0.315, 0.378);   // linear multiplier (ART_BIBLE §7, manifest outline)

let clipSet: THREE.AnimationClip[] = [];
export function setClips(g: GLTF) { clipSet = g.animations; }

const blobTex = (() => {      // the contact blob's falloff (a lighting term like a shadow map, not an art asset)
  const s = 64, c = document.createElement("canvas"); c.width = c.height = s;
  const g = c.getContext("2d")!; const r = g.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
  r.addColorStop(0, "rgba(90,70,54,1)"); r.addColorStop(0.55, "rgba(90,70,54,.55)"); r.addColorStop(1, "rgba(90,70,54,0)");
  g.fillStyle = r; g.fillRect(0, 0, s, s); const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; return t;
})();
const blobGeo = new THREE.PlaneGeometry(0.8, 0.62).rotateX(-Math.PI / 2);
const blobMat = new THREE.MeshBasicMaterial({ map: blobTex, transparent: true, opacity: 0.38, depthWrite: false });

/** the outline twin: same geometry + skeleton, pushed out along the averaged normal, albedo × OUTLINE_K, back faces */
function outlineMaterial(src: THREE.Material, thickness: number, hasOutlineNormal: boolean): THREE.MeshBasicMaterial {
  const map = (src as any).map as THREE.Texture | null;
  const vc = !!(src as any).vertexColors;
  const m = new THREE.MeshBasicMaterial({ map: map ?? null, vertexColors: vc, color: map || vc ? OUTLINE_K : new THREE.Color((src as any).color ?? 0xffffff).multiply(OUTLINE_K), side: THREE.BackSide });
  m.onBeforeCompile = (sh) => {
    sh.uniforms.uThick = { value: thickness };
    sh.vertexShader = (hasOutlineNormal ? "attribute vec3 _outlinenormal;\n" : "") + "uniform float uThick;\n" + sh.vertexShader.replace(
      "#include <begin_vertex>",
      `#include <begin_vertex>\n transformed += ${hasOutlineNormal ? "_outlinenormal" : "normalize(normal)"} * uThick;`);   // _outlinenormal length 0 = no outline on that part
  };
  m.customProgramCacheKey = () => `ol${hasOutlineNormal ? 1 : 0}${vc ? "v" : ""}`;
  return m;
}

export class Character {
  root = new THREE.Group();
  model: THREE.Object3D;
  mixer: THREE.AnimationMixer;
  skeleton: THREE.Skeleton | null = null;
  private actions = new Map<string, THREE.AnimationAction>();
  private cur: THREE.AnimationAction | null = null;
  curName: AnimName = "idle";
  ragdoll: Ragdoll | null = null;
  private blob: THREE.Mesh;
  speed = 0;                       // m/s on the ground (drives walk/run playback)
  scale: number;
  private attachments: THREE.Object3D[] = [];

  constructor(template: GLTF, opts: { skin?: number; hair?: string; body?: string; tier: Tier; outline?: boolean }) {
    this.model = cloneSkinned(template.scene);
    // the per-type scale is baked on the GLB's `root` joint (manifest conventions.characters) — read it, never re-apply it
    this.scale = this.model.getObjectByName("root")?.scale.x ?? 1;
    this.root.add(this.model);
    const meshes: THREE.SkinnedMesh[] = [];
    this.model.traverse((o: any) => { if (o.isSkinnedMesh || o.isMesh) meshes.push(o); });
    const tones: Partial<Record<1 | 2 | 3 | 4, THREE.Color>> = {};
    if (opts.skin !== undefined) tones[1] = SKIN[opts.skin % SKIN.length].clone().convertSRGBToLinear();
    if (opts.hair && HAIR[opts.hair]) tones[2] = new THREE.Color(HAIR[opts.hair]).convertSRGBToLinear();
    if (opts.body) { tones[3] = new THREE.Color(opts.body).convertSRGBToLinear(); tones[4] = tones[3].clone().multiplyScalar(0.18); }
    const toneKey = `${opts.skin ?? "-"}|${opts.hair ?? "-"}|${opts.body ?? "-"}`;
    for (const m of meshes) {
      if (Object.keys(tones).length) m.geometry = recolour(m.geometry, tones, toneKey);
      m.castShadow = opts.tier !== "low"; m.receiveShadow = false; m.frustumCulled = false;
      const mats = Array.isArray(m.material) ? m.material : [m.material];
      const out = mats.map((src: any) => {
        const t = new THREE.MeshToonMaterial({ gradientMap, map: src.map ?? null, color: src.color ?? 0xffffff, vertexColors: !!src.vertexColors, name: src.name });
        if (src.map) src.map.colorSpace = THREE.SRGBColorSpace;
        const glow = src.userData?.glow ?? 0;
        if (glow > 0) glowify(t, glow);
        else if (src.emissive && src.emissiveIntensity > 0 && src.emissive.getHex() !== 0) { t.emissive = src.emissive.clone(); t.emissiveIntensity = src.emissiveIntensity; t.emissiveMap = src.map ?? null; }
        return t;
      });
      m.material = Array.isArray(m.material) ? out : out[0];
      if ((m as any).isSkinnedMesh && !this.skeleton) this.skeleton = (m as THREE.SkinnedMesh).skeleton;
      if (opts.outline !== false) this.addOutline(m, mats);
    }
    if (!this.skeleton) this.model.traverse((o: any) => { if (o.isSkinnedMesh && !this.skeleton) this.skeleton = o.skeleton; });
    this.blob = new THREE.Mesh(blobGeo, blobMat); this.blob.position.y = 0.02; this.blob.scale.setScalar(Math.max(0.8, this.scale)); this.blob.renderOrder = -1;
    this.root.add(this.blob);
    this.mixer = new THREE.AnimationMixer(this.model);
    for (const c of clipSet) this.actions.set(c.name, this.mixer.clipAction(c));
    this.play("idle", 0);
  }

  private addOutline(m: THREE.Mesh, srcMats: THREE.Material[]) {
    const has = !!m.geometry.getAttribute("_outlinenormal");
    const mat = outlineMaterial(srcMats[0], 0.014, has);
    let twin: THREE.Mesh;
    if ((m as any).isSkinnedMesh) {
      const s = m as THREE.SkinnedMesh;
      const t = new THREE.SkinnedMesh(s.geometry, mat);
      t.bind(s.skeleton, s.bindMatrix);
      twin = t;
    } else twin = new THREE.Mesh(m.geometry, mat);
    twin.castShadow = false; twin.frustumCulled = false; twin.name = (m.name || "mesh") + "_outline";
    twin.position.copy(m.position); twin.quaternion.copy(m.quaternion); twin.scale.copy(m.scale);
    m.parent!.add(twin);
  }

  /** attach a cosmetic (mask, hat) to a bone; `t` = its local offset from the manifest */
  attach(obj: THREE.Object3D, bone: string) {
    let b: THREE.Object3D | undefined; this.model.traverse((o) => { if (!b && o.name === bone.replace(/\./g, "")) b = o; });
    (b ?? this.model).add(obj); this.attachments.push(obj);
  }
  detachAll() { for (const a of this.attachments) a.removeFromParent(); this.attachments = []; }

  /** switch the clip; `age` (s) lets a late joiner pick a looping call up mid-phrase so the crowd stays in sync */
  play(name: AnimName, fade = 0.18, age = 0) {
    if (name === "freeze") { if (this.cur) this.cur.timeScale = 0; this.curName = name; return; }
    if (name === "ragdoll") { this.curName = name; return; }
    const a = this.actions.get(name); if (!a) return;
    if (this.curName === name && this.cur === a) { a.timeScale = this.rateFor(name); return; }
    a.reset();
    a.setLoop(ONCE[name] ? THREE.LoopOnce : THREE.LoopRepeat, Infinity);
    a.clampWhenFinished = !!ONCE[name];
    a.timeScale = this.rateFor(name);
    if (age > 0) a.time = ONCE[name] ? Math.min(a.getClip().duration, age * a.timeScale) : (age * a.timeScale) % a.getClip().duration;
    a.enabled = true; a.setEffectiveWeight(1).play();
    if (this.cur && this.cur !== a) a.crossFadeFrom(this.cur, fade, false);
    this.cur = a; this.curName = name;
  }
  private rateFor(name: AnimName): number {
    if (name === "walk") return Math.max(0.6, Math.min(2.4, this.speed / (STRIDE.walk * this.scale)));
    if (name === "run") return Math.max(0.8, Math.min(2.2, this.speed / (STRIDE.run * this.scale)));
    const fit = FIT[name]; const a = this.actions.get(name);
    if (fit && a) return Math.max(1, Math.min(3.2, a.getClip().duration / fit));
    return 1;
  }

  /** start the physics tumble from the current pose */
  tumble(impulse: THREE.Vector3, spin = 0) {
    if (!this.skeleton) return;
    this.root.updateMatrixWorld(true);
    this.mixer.stopAllAction(); this.cur = null;
    this.ragdoll = new Ragdoll(this.root, this.skeleton, impulse, spin);
    this.curName = "ragdoll";
  }
  endTumble() { this.ragdoll = null; }

  private acc = 0;
  /** `far`: off the action — animate at half rate (low/mid tier); the mixer still gets the full elapsed time */
  update(dt: number, far = false) {
    if (this.ragdoll) { this.ragdoll.step(dt); this.blob.visible = false; return; }
    this.blob.visible = true;
    if (far) { this.acc += dt; if (this.acc < 1 / 30) return; dt = this.acc; }
    this.acc = 0;
    if (this.cur && (this.curName === "walk" || this.curName === "run")) this.cur.timeScale = this.rateFor(this.curName);
    this.mixer.update(dt);
  }

  dispose() { this.root.removeFromParent(); this.mixer.stopAllAction(); }
}
